import { FadeUp, SectionHead } from "@/components/Reveal";
import StatsBand from "@/components/StatsBand";
import EditorialMarquee from "@/components/Marquee";
import { ASSETS, IMAGES, LEADERSHIP } from "@/data/site";

const TIMELINE = [
  { year: "1965", title: "Waghe Namkeen is born", text: "Our grandfather begins crafting namkeen in Barwani, Madhya Pradesh — one kadhai, one family recipe, one uncompromising standard." },
  { year: "Decades of Trust", title: "A name the region swears by", text: "Through dedication, hard work and customer trust, the small family business becomes a household name across the Nimar region." },
  { year: "Incorporation", title: "Waghe & Sons Food Pvt. Ltd.", text: "The family business evolves into a modern private limited company — organised manufacturing, quality systems and a 50+ skilled workforce." },
  { year: "Today", title: "Two brands, one world stage", text: "Waghe Shree leads the Indian market while Tasty Mint is developed for international and export markets — Made in India, Served Worldwide." },
];

const MANIFESTO = [
  { n: "01", title: "Heritage First", text: "Recipes written by our grandfather's hands still guide every batch we fry today." },
  { n: "02", title: "Honest Ingredients", text: "Carefully selected grains and dals, premium edible oil, pure traditional spices. Nothing else." },
  { n: "03", title: "Craft at Scale", text: "A modern, hygienic production floor that protects the taste of a family kitchen." },
  { n: "04", title: "Trust, Exported", text: "The same standard that Barwani trusted for 60 years is what we ship to the world." },
];

export default function About() {
  return (
    <>
      <section className="relative pt-40 pb-24 sm:pb-32 bg-ink overflow-hidden" data-testid="about-hero">
        <img src={IMAGES.starAnise} alt="Pure traditional spices" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[11px] sm:text-xs uppercase tracking-overline text-gold mb-6">About Us · Est. 1965</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[1.02] text-cream max-w-4xl">
              Sixty years of craft,<br />from <em className="text-gold italic">Barwani</em> to the world.
            </h1>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-ink" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead
            testId="about-story-head"
            overline="Our Story"
            title="The journey from Waghe Namkeen to Waghe & Sons."
          />
          <div className="mt-14 grid lg:grid-cols-12 gap-12">
            <FadeUp className="lg:col-span-5">
              <div className="image-frame border border-gold/30 lg:sticky lg:top-32">
                <img src={ASSETS.banner} alt="Waghe & Sons — factory, brands and product portfolio" className="w-full h-auto" data-testid="about-corporate-banner" />
              </div>
            </FadeUp>
            <div className="lg:col-span-7 space-y-0">
              {TIMELINE.map((t, i) => (
                <FadeUp key={t.title} delay={i * 0.08} className="relative pl-10 pb-12 border-l border-white/10 last:pb-0">
                  <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rotate-45 bg-gold" />
                  <p className="text-[11px] uppercase tracking-overline text-gold">{t.year}</p>
                  <h3 className="mt-2 font-display text-2xl text-cream">{t.title}</h3>
                  <p className="mt-3 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-xl">{t.text}</p>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-coal border-y border-white/10" data-testid="about-leadership">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead
            testId="about-leadership-head"
            overline="Leadership · The Next Generation"
            title="Guided by family, driven by craft."
            description="The third generation of the Waghe family now leads the house — carrying their grandfather's recipe and their father Shyam Waghe's discipline into a global era."
          />
          <div className="mt-16 grid sm:grid-cols-2 gap-6 max-w-4xl">
            {LEADERSHIP.map((l, i) => (
              <FadeUp key={l.name} delay={i * 0.12}>
                <div
                  data-testid={l.testId}
                  className="group relative bg-ink border border-white/10 rounded-sm overflow-hidden transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 hover:border-gold/50 hover:shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
                >
                  <div className="relative aspect-[4/4.2] overflow-hidden">
                    <img src={l.img} alt={`${l.name}, ${l.role} — Waghe & Sons Food Pvt. Ltd.`} className="w-full h-full object-cover grayscale-[35%] transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-gold/0 via-gold to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6">
                    <p className="text-[10px] uppercase tracking-[0.26em] text-gold">{l.role} · {l.lineage}</p>
                    <h3 className="mt-2 font-display text-2xl sm:text-3xl text-cream">{l.name}</h3>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-ink" data-testid="about-manifesto">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead testId="about-manifesto-head" overline="The Waghe Manifesto" title="Four promises we fry into every batch." />
          <div className="mt-16 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {MANIFESTO.map((m, i) => (
              <FadeUp key={m.n} delay={i * 0.08} className="bg-ink p-8 sm:p-12 group hover:bg-coal transition-colors duration-500">
                <p className="font-display text-5xl text-outline-gold group-hover:text-gold transition-colors duration-500">{m.n}</p>
                <h3 className="mt-5 font-display text-2xl text-cream">{m.title}</h3>
                <p className="mt-3 text-sm md:text-base text-zinc-400 font-light leading-relaxed">{m.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <StatsBand />
      <EditorialMarquee />
    </>
  );
}
